import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import classNames from 'classnames';

//material ui
import { Button, Collapse } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import AttachFileIcon from '@material-ui/icons/AttachFile';

import { FormattedMessageWrappedInSpan } from '../../misc';
import GDPRHint from '../GDPRHint';
import checkFileExtension from '../../../utils/checkFileExtension';
import { formatSnWithMaybeUndefinedVal } from '../../../utils/common';
import UploadGpxFileMutation from '../../../mutations/UploadGpxFileMutation';

import environment from '../../../environment';

const ExtendedSearchGpsSearchTab = (props) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileExtensionError, setShowFileExtensionError] = useState(false);
  const [showFileSizeError, setShowFileSizeError] = useState(false);
  const [showSearchBtn, setShowSearchBtn] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChanged = (event) => {
    let selectedFile;
    selectedFile = event.target.files[0];

    // 'selectedFile' can be undefined if no file has been selected in the file manager
    if (selectedFile) {
      // Check if the file selected by the user is a "gpx" file)
      if (checkFileExtension(selectedFile.name, 'gpx')) {
        // The backend currently supports file uploads up to 20.971.520 bytes (i.e. 20 MB)
        if (selectedFile.size > 20971520) {
          setSelectedFile(null);
          setShowFileExtensionError(false);
          setShowFileSizeError(true);
          setShowSearchBtn(false);
          return;
        }
        setSelectedFile(selectedFile);
        setShowFileExtensionError(false);
        setShowFileSizeError(false);
        setShowSearchBtn(true);
      } else {
        setSelectedFile(null);
        setShowFileExtensionError(true);
        setShowFileSizeError(false);
        setShowSearchBtn(false);
      }
    }
  };

  const handleUploadGpxFile = () => {
    const { eventId, startNumber, onNavigateTo } = props;
    let formattedstartNumber = formatSnWithMaybeUndefinedVal(startNumber);
    if (selectedFile) {
      setIsUploading(true);
      UploadGpxFileMutation.commit(environment, selectedFile, (error, fileId) => {
        if (error) {
          setIsUploading(false);
        } else {
          setIsUploading(false);
          onNavigateTo(`/event/${eventId}/gps/${fileId}/${formattedstartNumber}`);
        }
      });
    }
  };

  return (
    <div className="extended-search-gpssearch-container">
      <div>
        <p>
          <FormattedMessageWrappedInSpan
            id="extendedSearchGpsSearchTab.ownSearch.descriptionWithoutStrava"
            defaultMessage="Upload your GPX file and we will show your photos"
          />
        </p>
        <div className="gps-search-columns">
          {/* manual upload */}
          <div
            className={classNames('gps-search-column', {
              'gps-search-column_selected': selectedFile,
            })}
          >
            <Collapse
              in={showFileSizeError || showFileExtensionError || isUploading}
              data-test-id="manual-upload-options"
            >
              <span className="gps-search-column-label">
                {showFileExtensionError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.fileExtensionError"
                    defaultMessage="The file should have the following extension: .gpx"
                    className="typography warning-text bold-text"
                  />
                )}
                {showFileSizeError && (
                  <FormattedMessageWrappedInSpan
                    id="extendedSearchGpsSearchTab.ownSearch.fileSizeError"
                    defaultMessage="The file is too big. We currently support file uploads up to 20 MB."
                    className="typography warning-text bold-text"
                  />
                )}
                {isUploading && <CircularProgress size={40} thickness={3} />}
              </span>
            </Collapse>

            <Button
              classes={{ root: 'gps-search-upload-btn' }}
              data-testid="manual-gpx-upload-btn"
            >
              <AttachFileIcon />
              {selectedFile ? (
                <span>{selectedFile.name}</span>
              ) : (
                <FormattedMessageWrappedInSpan
                  id="extendedSearchGpsSearchTab.ownSearch.uploadOwnFileButton"
                  defaultMessage="Upload own GPX file"
                />
              )}
              <input type="file" onChange={(e) => handleFileChanged(e)} />
            </Button>
          </div>
        </div>
        <Collapse
          in={!isUploading && showSearchBtn}
          className={classNames({
            'mt-20': !isUploading && showSearchBtn,
          })}
        >
          <button className="btn btn-default primary-new" onClick={handleUploadGpxFile}>
            <FormattedMessageWrappedInSpan
              id="extendedSearchGpsSearchTab.ownSearch.searchButton"
              defaultMessage="Search"
            />
          </button>
        </Collapse>
      </div>
      <GDPRHint className="mt-30" />
    </div>
  );
};

ExtendedSearchGpsSearchTab.propTypes = {
  eventId: PropTypes.number,
  startNumber: PropTypes.string,
  onNavigateTo: PropTypes.func
};

export { ExtendedSearchGpsSearchTab };
export default createFragmentContainer(ExtendedSearchGpsSearchTab, {
  eventDates: graphql`
    fragment ExtendedSearchGpsSearchTab_eventDates on Event {
      beginsAt
      endsAt
    }
  `,
});
